import FeedItem from '@app/components/FeedItem'
import {
  FeedItemScope,
  IClearFeedItemsActionCreator,
} from '@app/components/FeedItem/modules/feedItems'
import { IActivityFeedItem } from '@app/models/ActivityFeedItem'
import { ICurrentUserState } from '@app/store/modules/account'
import { bindFunctions } from '@app/utils/class'
import { getRLContentContainer } from '@app/utils/window'
import * as React from 'react'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Index,
  IndexRange,
  InfiniteLoader,
  List,
  ListRowProps,
  WindowScroller,
} from 'react-virtualized'
import { Alert, Nav, NavItem, NavLink } from 'reactstrap'
import * as ActivityFeedModule from '../modules/activityFeed'

interface IActivityFeedUIProps {
  contextId?: string
  filter?: string
}

type ActivityFeedProps = ActivityFeedModule.IActivityFeedState &
  typeof ActivityFeedModule.actionCreators &
  IActivityFeedUIProps &
  ICurrentUserState &
  IClearFeedItemsActionCreator

export default class ActivityFeed extends React.PureComponent<
  ActivityFeedProps
> {
  private cache: CellMeasurerCache
  private scrollElement?: Element
  private list: List | null
  private loader: InfiniteLoader | null
  private loadNewIntervalId?: number

  constructor(props: ActivityFeedProps) {
    super(props)

    bindFunctions(this)

    this.cache = new CellMeasurerCache({
      defaultHeight: 100,
      fixedWidth: true,
      keyMapper: this._cacheKeyMapper,
    })
  }

  public componentDidMount() {
    this.props.setEntityId(this.props.contextId)
    if (this.props.contextId) {
      this.props.setScope(FeedItemScope.All)
    }

    this.props.setFilter(this.props.filter)

    this.props.loadActivityFeed(0, 25, false, this.props.filter)

    this.loadNewIntervalId = window.setInterval(this._loadNewFeedItems, 10000)
  }

  public componentDidUpdate(prevProps: ActivityFeedProps) {
    if (this.props.itemChangedIndex !== undefined) {
      this._onItemLayoutChanged(this.props.itemChangedIndex)
      this.props.resetItemChangedIndex()
    }
  }

  public componentWillUnmount() {
    this.props.resetActivityFeed()
    this.props.clearFeedItems()
    if (this.loadNewIntervalId) {
      window.clearInterval(this.loadNewIntervalId)
      this.loadNewIntervalId = undefined
    }

    // this is a hack for fixing an issue(github.com/gatewayapps/haagbrown-app/issues/391) in mobile Safari
    if (this.scrollElement) {
      this.scrollElement.setAttribute('style', 'overflow-y:hidden')
      const that = this.scrollElement
      setTimeout(() => {
        that.setAttribute('style', 'overflow-y:auto')
      }, 1)
    }
  }

  public render() {
    if (!this.scrollElement) {
      this.scrollElement = getRLContentContainer()
    }

    return (
      <div>
        {!this.props.filter && this._renderScopeToggle()}
        {this._renderNewItemsNotification()}
        <WindowScroller
          scrollElement={this.scrollElement}
          onResize={this._onResize}
        >
          {({
            height,
            isScrolling,
            onChildScroll,
            registerChild,
            scrollTop,
          }) => (
            <AutoSizer disableHeight>
              {({ width }) => (
                <div ref={registerChild as any}>
                  <InfiniteLoader
                    isRowLoaded={this._isRowLoaded}
                    loadMoreRows={this._loadMoreRows}
                    minimumBatchSize={20}
                    ref={(el) => (this.loader = el)}
                    rowCount={this.props.totalItems}
                  >
                    {({ onRowsRendered, registerChild: loaderChild }) => (
                      <List
                        autoHeight
                        className='mt-3'
                        deferredMeasurementCache={this.cache}
                        height={height || 0}
                        isScrolling={isScrolling}
                        noRowsRenderer={this._noRowsRenderer}
                        onRowsRendered={onRowsRendered}
                        onScroll={onChildScroll}
                        overscanRowCount={3}
                        ref={(el) => {
                          this.list = el
                          loaderChild(el)
                        }}
                        rowCount={this.props.items.size}
                        rowHeight={this.cache.rowHeight}
                        rowRenderer={this._rowRenderer}
                        scrollTop={scrollTop}
                        width={width}
                      />
                    )}
                  </InfiniteLoader>
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      </div>
    )
  }

  private _cacheKeyMapper(rowIndex: number, columnIndex: number) {
    const item = this._getItem(rowIndex)
    return item ? item.id : `${rowIndex}-${columnIndex}`
  }

  private _getItem(index: number): IActivityFeedItem | undefined {
    return this.props.items.get(index)
  }

  private _isRowLoaded({ index }: Index) {
    if (this.props.items.size <= index) {
      return false
    }
    return !!this._getItem(index)
  }

  private _loadFeedItems(
    startIndex: number,
    stopIndex: number,
    append: boolean,
  ): Promise<void> {
    return new Promise((resolve) => {
      this.props.loadActivityFeed(
        startIndex,
        stopIndex,
        append,
        this.props.filter,
        resolve,
      )
    })
  }

  private _loadNewFeedItems() {
    if (this.props.filter) {
      return
    }

    if (!this.props.isLoadingNewItems) {
      this.props.loadNewActivityFeed()
    }
  }

  private _loadMoreRows({ startIndex, stopIndex }: IndexRange): Promise<void> {
    if (this.props.isLoading) {
      return Promise.resolve()
    }

    return this._loadFeedItems(startIndex, stopIndex, true)
  }

  private _noRowsRenderer() {
    return <div>No Feed Items</div>
  }

  private _onChangeScope(newScope: FeedItemScope) {
    this.props.setScope(newScope)
    if (this.loader) {
      this.loader.resetLoadMoreRowsCache(true)
    }
    this._loadFeedItems(0, 25, false).then(() => {
      this.cache.clearAll()
      if (this.list) {
        this.list.forceUpdateGrid()
      }
    })
  }

  private _onItemLayoutChanged(index?: number) {
    if (index === undefined) {
      this.cache.clearAll()
      if (this.list) {
        this.list.recomputeRowHeights()
      }
    } else {
      this.cache.clear(index, 0)
      if (this.list) {
        this.list.recomputeRowHeights(index)
      }
    }
  }

  private _onResize({ height, width }: { height: number; width: number }) {
    this._onItemLayoutChanged()
  }

  private _onShowNewItems() {
    this.props.showNewItems()
  }

  private _renderNewItemsNotification() {
    if (this.props.newItems.size === 0) {
      return null
    }

    return (
      <div
        className='text-center cursor-pointer mx-2 mt-3'
        onClick={this._onShowNewItems}
      >
        <Alert className='alert-hover' color='info'>
          See {this.props.newItems.size} new{' '}
          {this.props.newItems.size > 1 ? 'Posts' : 'Post'}
        </Alert>
      </div>
    )
  }

  private _renderScopeToggle() {
    if (this.props.contextId) {
      return null
    }

    if (!this.props.user || !this.props.user.permissions.isAdmin) {
      return null
    }

    return (
      <div className='mt-3'>
        <Nav tabs>
          <NavItem>
            <NavLink
              active={this.props.scope === FeedItemScope.Mine}
              onClick={() => this._onChangeScope(FeedItemScope.Mine)}
            >
              {FeedItemScope.Mine}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={this.props.scope === FeedItemScope.All}
              onClick={() => this._onChangeScope(FeedItemScope.All)}
            >
              {FeedItemScope.All}
            </NavLink>
          </NavItem>
        </Nav>
      </div>
    )
  }

  private _rowRenderer({ key, index, parent, style }: ListRowProps) {
    const feedItem = this.props.items.get(index)

    if (!feedItem) {
      return (
        <div key={key} style={style}>
          Loading {index}
        </div>
      )
    }

    return (
      <CellMeasurer
        cache={this.cache}
        columnIndex={0}
        key={feedItem.id}
        parent={parent}
        rowIndex={index}
      >
        <div key={feedItem.id} style={style}>
          <FeedItem
            feedItemId={feedItem.id}
            onLayoutChanged={() => {
              this.props.feedItemLayoutChanged(index, feedItem.id)
            }}
          />
        </div>
      </CellMeasurer>
    )
  }
}
